<template>
  <div class="multi-selection-table-container no-select scroll-fire-fox">
    <v-progress-linear
      v-if="deletingObjects || loading"
      indeterminate
      color="#4fa2b7"
      height="7"
      style="margin-top: 0; height: 3px"
    />
    <slot name="header" />

    <div
      v-if="!loading && fields.length === 0 && objects.length === 0"
      class="load-data-button"
    >
      <v-btn
        class="mt-3 text-none"
        color="accent"
        x-small
        text
        style="font-size: 12px;"
        @click="updateTable"
        :loading="loading"
      >
        {{ $t("ClickLoadData") }}
      </v-btn>
    </div>
    <table
      v-else-if="!loading && !editSelectedOpen"
      class="multi-selection-table"
      id="table"
    >
      <tr class="table-header">
        <th class="object-index" />

        <th>
          ID
          <!--<v-btn flat icon @click="sortTable(1)">
          <v-icon>keyboard_arrow_down</v-icon>
          </v-btn>-->
        </th>

        <th
          v-for="field in fields"
          :key="field.id"
          class="header-cell"
          :class="{ required: field.required }"
        >
          {{ field.label }}
          <!--<v-btn flat icon @click="sortTable(1)">
          <v-icon>keyboard_arrow_down</v-icon>
          </v-btn>-->
        </th>
      </tr>

      <tr
        class="table-body"
        v-for="(object, objectIndex) in objects"
        :key="objectIndex"
        :class="{
          selected: selected.includes(objectIndex)
        }"
      >
        <td class="object-index" @click="select(objectIndex, $event)">
          {{ objectIndex + 1 }}
        </td>

        <td class="input-disabled" @click="select(objectIndex, $event)">
          {{ object[initials + "_id"] }}
        </td>

        <td
          v-for="(field, fieldIndex) in fields"
          :key="field.id"
          :class="editionMode ? field.size : ''"
          :ref="getInputRef(objectIndex, fieldIndex)"
        >
          <div
            class="input-disabled with-select"
            v-if="
              (!editionMode || (editionMode && field.disable)) &&
                field.input_type !== 'checkbox'
            "
            @click="select(objectIndex, $event)"
          >
            {{ getNonEditableValue(object, field) }}
          </div>

          <input
            v-else-if="isStandardInput(field.input_type)"
            :type="getSpecificStandardInputType(field.input_type)"
            :value="getValue(object, field)"
            @change="setValue(objectIndex, field, $event.target.value)"
            class="table-input"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            @keydown.ctrl.up.prevent="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />

          <treeselect
            v-else-if="
              isSelectInput(field.input_type) &&
                field.var_name === prefix + '_material_id'
            "
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="materialOptions"
            :normalizer="materialOptionsNormalizer"
            :load-options="loadMaterialOptions"
            :clearable="selectIsClearable(field)"
            :disabled="true"
            class="multi-selection-select"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
            @keydown.ctrl.up.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />

          <treeselect
            v-else-if="isSelectInput(field.input_type)"
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="getSpecificListOfOptions(field, getValue(object, field))"
            :clearable="selectIsClearable(field)"
            :disabled="field.multi_select_disable"
            class="multi-selection-select"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
            @keydown.ctrl.up.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />
          <treeselect
            v-else-if="field.input_type === 'user'"
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="optionsUser"
            :normalizer="userNormalizer"
            :load-options="loadUserOptions"
            :clearable="selectIsClearable(field)"
            :disabled="field.multi_select_disable"
            class="multi-selection-select"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
            @keydown.ctrl.up.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />
          <div
            v-if="field.input_type === 'checkbox'"
            class="multi-selection-checkbox-container"
            :class="{ edited: valueWasChanged(objectIndex, field) }"
          >
            <v-checkbox
              :input-value="valueCheckboxInObject(object, field)"
              @change="setValue(objectIndex, field, $event)"
              class="multi-selection-checkbox"
              style="z-index: 100"
              color="primary"
              :ripple="false"
              :disabled="!editionMode"
              :hide-details="true"
              disableFuzzyMatching
              @keydown.ctrl.up.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, 1)
              "
            />
          </div>
        </td>
      </tr>
    </table>

    <div
      v-if="!loading && fields.length > 0 && objects.length === 0"
      class="table-warning"
    >
      {{ $t("NoObjectsOfTypeInSelectedArea") }}
    </div>

    <multi-selection-edit-selected
      v-if="editSelectedOpen"
      :fields="fields"
      :objects="objects"
      :selected="selected"
      :options="options"
      :optionsUser="optionsUser"
      @close="closeEditSelected"
      @apply="applyMassEdition"
    />
    </div>
</template>

<script>
import { bus } from "@/main.js";
import { mapActions } from "vuex";
import _ from "lodash";
import MultiSelectionEditSelected from "./MultiSelectionEditSelected";
import { multiSelectionMixin } from "@/mixins/multiSelectionMixin";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";

export default {
  name: "MultiSelectionTable",

  components: {
    AppDialogBox,
    MultiSelectionEditSelected,
  },

  mixins: [multiSelectionMixin],

  props: {
    /**
     * Layer corresponding to the selected tab.
     */
    layer: {
      type: String,
      required: true
    },

    canHighlightObjects: {
      type: Boolean
    }
  },

  data() {
    return {
      loadingStructure: false,
      fields: [],
      optionsUser: undefined,
      /**
       * List of options the materials
       */
      materialOptions: null,
      initials: undefined,

      loadingObjects: false,
      originalObjects: [],
      objects: [],
      selected: [],
      lastItemClicked: undefined,

      editionMode: false,
      dataToSend: [],
      editSelectedOpen: false,

      closuresDelete: [],

      deletingObjects: false
    };
  },

  computed: {
    /**
     * Determines the endpoint to use to make any requests.
     */
    endpoint() {
      return this.$store.getters.getLayerEndpoint(this.layer);
    },

    /**
     * Determines the prefix of the current.
     */
    prefix() {
      return this.$store.getters.getLayerInitials(this.layer);
    },

    /**
     * Determines if the datatable content is loading or not.
     * @return {Boolean}
     */
    loading() {
      return this.loadingStructure || this.loadingObjects || this.deletingObjects;
    },

    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberObjects() {
      return this.objects.length;
    },

    /**
     * Determines the number of objects selected
     * @return {Number}
     */
    numberSelectedObjects() {
      return this.selected.length;
    },

    /**
     * Determines if any objects have been edited or not.
     */
    objectsWereEdited() {
      return this.dataToSend.length > 0;
    },

    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberFields() {
      return this.fields.length;
    },

    dataToExportHeaders() {
      let headers = { id: "ID" };
      for (const field of this.fields) {
        headers[field.var_name] = field.multi_select_label
          ? field.multi_select_label
          : field.label;
      }
      return headers;
    }
  },

  watch: {
    /**
     * Updates data structure when the object type is updated.
     */
    layer() {
      this.updateTable();
    },

    /**
     * Sends an update to the toolbar regarding the total number of objects.
     * @param {Number} newValue - new total of objects
     */
    numberObjects(newValue) {
      bus.$emit("multiple-selection-update-number-objects", newValue);
    },

    /**
     * Sends an update to the toolbar regarding the number of select objects.
     * @param {Number} newValue - new number of selected objects
     */
    numberSelectedObjects(newValue) {
      bus.$emit("multiple-selection-update-number-selected-objects", newValue);
    },

    /**
     * Sends an update to the toolbar regarding the presence of changed objects.
     * @param {Boolean} newValue - if there are edited objects or not
     */
    objectsWereEdited(newValue) {
      bus.$emit("multiple-selection-update-edition-state", newValue);
    },

    loading() {
      this.$emit("loadingObjects", this.loading);
    }
  },

  mounted() {
    this.receiveInstructionsFromToolbar();
    this.loadMaterialOptions();
  },

  destroyed() {
    bus.$off("multiple-selection-update-mode");
    bus.$off("multiple-selection-activate-edit-selected");
    bus.$off("multiple-selection-save");
    bus.$off("multiple-selection-delete");
    bus.$off("multiple-selection-clone");
    bus.$off("multiple-selection-select-all");
    bus.$off("multiple-selection-invert-selection");
    bus.$off("multiple-selection-clear-selection");
    bus.$off("multiple-selection-refresh");
    bus.$off("multiple-selection-delete-closure");
    bus.$off("multiple-selection-get-data-to-export");
  },

  methods: {
    ...mapActions([
      "FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE",
      "GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON",
      "DELETE_OBJECTS",
      "CLONE_OBJECTS",
      "EDIT_OBJECTS",
      "fetchAllUsers",
      "fetchStockMaterialsList"
    ]),

    /**
     * Updates the table structure and content.
     */
    updateTable() {
      this.clearDataToSend();
      this.getTableStructure();
      this.getObjects();
    },

    /**
     * Retrieves the current tab object type from the store.
     */
    getTableStructure() {
      this.fields = [];
      this.loadingStructure = true;
      bus.$emit("change-multi-selection-toolbar-state", false);
      this.FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE({
        layer: this.layer,
        needsTabs: false,
        needsFields: true
      })
        .then(data => {
          let formFields = data.fields;
          this.initials = data.initials;
          formFields.forEach( (field,index)=>{
            if (
              field.hasOwnProperty("multi_select_show") &&
              field.hasOwnProperty("multi_select_label") &&
              field.multi_select_show &&
              !field.hide
            ) {
              field.label = field.multi_select_label;
              if (field.input_type === "checkbox") {
                this.getCheckboxFields(field);
              } else {
                if (
                  field.input_type === "select" &&
                  field.hasOwnProperty("select_list_name")
                ) {
                  this.getSelectOptions(field.select_list_name);
                } else if (field.input_type === "radio") {
                  this.getRadioOptions(field);
                }

                this.fields.push(field);
              }
            }
          })
          let labelIndex = this.fields.map(field => field.label).indexOf("Label");
          if(labelIndex>-1){
            this.fields.splice(labelIndex +1,0,
              {
                id: "labelIndex",
                input_type:"checkbox",
                label:"Label Lock",
                required:false,
                multi_select_disable:false,
                var_name: this.initials + "_label_locked",
              }
            );
          }
          this.loadingStructure = false;
          bus.$emit("change-multi-selection-toolbar-state", true);
        })
        .catch(error => console.error(error));
    },

    /**
     * Obtains the objects of the currently selected layer present in the selected area.
     */
    getObjects() {
      this.selected = [];
      this.originalObjects = [];
      this.objects = [];
      this.loadingObjects = true;
      let technicalType = undefined;
      if (this.endpoint.includes("technical")) {
        technicalType = this.layer;
      }
      this.GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON({
        endpoint: this.endpoint,
        technicalType: technicalType
      })
        .then(objects => {
          let filteredObjects = this.filterObjects(
            objects,
            this.layer.includes("base")
          );
          this.originalObjects = JSON.parse(JSON.stringify(filteredObjects));
          this.objects = filteredObjects;
          this.loadingObjects = false;
        })
        .catch(error => {
          console.error(error);
          this.loadingObjects = false;
        });
    },

    /**
     * Gets only the objects corresponding to the given layer.
     * @param {Array} objectList - list of objects returned by the server
     * @param {Boolean} addIfBlocked - determines if it's the blocked or unblocked objects that are to be added to
     * the current list of objects.
     * @returns {Array}
     */
    filterObjects(objectList, addIfBlocked) {
      let filteredObjects = [];
      let blockedVar = this.initials + "_blocked";
      if (objectList.length > 0 && objectList[0].hasOwnProperty(blockedVar)) {
        objectList.forEach(object => {
          if (
            (addIfBlocked && object[blockedVar]) ||
            (!addIfBlocked && !object[blockedVar])
          ) {
            filteredObjects.push(object);
          }
        });
      } else {
        filteredObjects = objectList;
      }
      return filteredObjects;
    },

    /**
     * Objects the UUID value of a given object
     * @param {Object} object
     * @returns {String}
     */
    getObjectUUID(object) {
      return object[this.initials + "_uuid"];
    },

    /**
     * Opens the necessary channels to receive instructions from the toolbar.
     */
    receiveInstructionsFromToolbar() {
      bus.$on("multiple-selection-update-mode", isActive =>
        this.updateMode(isActive)
      );
      bus.$on("multiple-selection-activate-edit-selected", () => {
        this.editSelectedOpen = true;
      });
      bus.$on("multiple-selection-save", () => this.save());
      bus.$on("multiple-selection-delete", () =>
        this.remove(this.getSelectedUUIDs())
      );
      bus.$on("multiple-selection-clone", () => this.clone());
      bus.$on("multiple-selection-select-all", () => this.selectAll());
      bus.$on("multiple-selection-invert-selection", () =>
        this.invertSelection()
      );
      bus.$on("multiple-selection-clear-selection", () =>
        this.clearSelection()
      );
      bus.$on("multiple-selection-refresh", () => this.refresh());
      bus.$on("multiple-selection-delete-closure", () => this.deleteClosures());
      bus.$on("multiple-selection-get-data-to-export", () =>
        this.getDataToExport()
      );
    },

    /**
     * Determines if the user if making a simple select or a bulk select and executes the correct action.
     * @param {Number} index - index of the object clicked
     * @param {Object} e - information about the click event
     */
    select(index, e) {
      if (e.shiftKey) {
        this.bulkSelect(index);
      } else {
        this.toggleObject(index);
      }
      this.lastItemClicked = index;
    },

    /**
     * Allows selecting a group of items in bulk.
     * @param index - index of the last clicked item.
     */
    bulkSelect(index) {
      let previousClick = 0;
      let firstItem;
      let lastItem;
      if (this.lastItemClicked !== undefined) {
        previousClick = this.lastItemClicked;
      }
      if (previousClick === index) {
        this.toggleObject(index);
      } else {
        if (previousClick < index) {
          firstItem = previousClick;
          lastItem = index;
        } else if (previousClick > index) {
          firstItem = index;
          lastItem = previousClick;
        }
        for (let i = firstItem; i <= lastItem; i++) {
          if (!this.selected.includes(i)) {
            let geoJson = this.getGeoJson(i);
            let uuid = this.getUuid(i);
            if (geoJson && this.canHighlightObjects) {
              bus.$emit("highlight-object-from-selected-table", {
                geoJson: geoJson,
                uuid: uuid
              });
            }
            this.selected.push(i);
          }
        }
      }
    },

    /**
     * Adds or removes an object index from the selected list
     * @param {number} index -  index of the object in the objects list
     */
    toggleObject(index) {
      let geoJson = this.getGeoJson(index);
      let uuid = this.getUuid(index);

      let selectedIndex = this.selected.findIndex(
        selected => selected === index
      );
      if (selectedIndex !== -1) {
        this.selected.splice(selectedIndex, 1);
        if (geoJson && this.canHighlightObjects) {
          bus.$emit("remove-highlight-from-selected-table", uuid);
        }
      } else {
        if (geoJson && this.canHighlightObjects) {
          bus.$emit("highlight-object-from-selected-table", {
            geoJson: geoJson,
            uuid: uuid
          });
        }
        this.selected.push(index);
      }
    },

    getGeoJson(index) {
      let geoJson;
      let object = this.objects[index];
      if (this.endpoint === "technicalpoints") {
        geoJson = object.node.nd_geo_json;
      } else {
        geoJson = object[this.initials + "_geo_json"];
      }
      return geoJson;
    },

    getUuid(index) {
      let object = this.objects[index];
      return object[this.initials + "_uuid"];
    },

    /**
     * Adds all objects to the selected list.
     */
    selectAll() {
      this.clearSelection();
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.selected.push(i);
        let geoJson = this.getGeoJson(i);
        let uuid = this.getUuid(i);
        if (geoJson && this.canHighlightObjects) {
          bus.$emit("highlight-object-from-selected-table", {
            geoJson: geoJson,
            uuid: uuid
          });
        }
      }
    },

    /**
     * Inverts the selection, i.e. adds the previously non-selected objects to the list and removes the previously
     * selected from the list.
     */
    invertSelection() {
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.toggleObject(i);
      }
    },

    /**
     * Removes all objects from the selected list.
     */
    clearSelection() {
      this.selected = [];
      bus.$emit("remove-highlight-from-selected-table");
    },

    /**
     * Creates a list of the UUIDs of the selected objects.
     */
    getSelectedUUIDs() {
      let selectedUUIDs = [];
      if (this.endpoint === "technicalpoints") {
        selectedUUIDs = {
          uuids: [],
          extra_data: {
            join_pathways: "true"
          }
        };

        this.selected.forEach(objectIndex => {
          selectedUUIDs.uuids.push(
            this.getObjectUUID(this.objects[objectIndex])
          );
        });
      } else {
        this.selected.forEach(objectIndex => {
          selectedUUIDs.push({
            uuid: this.getObjectUUID(this.objects[objectIndex])
          });
        });
      }

      return selectedUUIDs;
    },

    /**
     * Eliminates the selected objects from the database.
     */
    remove(uuidOfObjects) {
      this.deletingObjects = true;
      this.DELETE_OBJECTS({
        endpoint: this.endpoint,
        objects: uuidOfObjects
      })
        .then(() => {
          if (this.endpoint === "closures") {
            this.closuresDelete = {
              dialogOpen: false,
              associatedCables: null
            };
          }

          this.refresh();
        })
        .catch(error => {
          if (error.response.data.status_code === 423) {
            this.$toasted.global.toast_error(
              this.$t("DeletePoleWithAssociatedClosure")
            );
          } else {
            console.error(error);
          }

          if (this.endpoint === "closures") {
            this.closuresDelete = {
              dialogOpen: true,
              associatedCables: error.response.data.data,
              selected: this.selected
            };
          }
        })
        .then(() => {
          this.$nextTick(() =>
            this.$emit("closureDelete", this.closuresDelete)
          );
          this.deletingObjects = false;
        });
    },

    deleteClosures() {
      let uuidOfObjectsSelected = this.getSelectedUUIDs();
      let uuidOfObjectsDeleted = [];

      uuidOfObjectsSelected.forEach(uuidObject => {
        let objectIsRemove = false;
        if (this.closuresDelete && this.closuresDelete.associatedCables) {
          Object.keys(this.closuresDelete.associatedCables).forEach(
            uuidClosure => {
              if (uuidClosure === uuidObject.uuid) {
                objectIsRemove = true;
              }
            }
          );
        }
        if (!objectIsRemove) {
          uuidOfObjectsDeleted.push(uuidObject);
        }
      });
      if (uuidOfObjectsDeleted.length > 0) {
        this.remove(uuidOfObjectsDeleted);
      } else {
        this.closuresDelete = {
          dialogOpen: false,
          associatedCables: null,
          selected: null
        };
        this.$emit("closureDelete", this.closuresDelete);
      }
    },

    /**
     * Clones the selected objects (creates new objects with the same information)
     */
    clone() {
      this.CLONE_OBJECTS({
        endpoint: this.endpoint,
        data: this.getSelectedUUIDs()
      })
        .then(() => {
          this.refresh();
        })
        .catch(error => {
          console.error(error);
        });
    },

    /**
     * Updates the state of the edition mode.
     * @param {Boolean} isActive - if the edition mode is active or not
     */
    updateMode(isActive) {
      if (this.objectsWereEdited) {
        this.clearDataToSend();
        this.refresh();
      }
      this.editionMode = isActive;
    },

    /**
     * Get the field value in non edition mode.
     * @param {Object} object
     * @param {Object} field
     * @returns {Number | String}
     */
    getNonEditableValue(object, field) {
      let value = this.getValue(object, field);
      if (value === null || value === undefined || value === "") {
        value = "-";
      } else if (
        field.input_type === "select" &&
        field.hasOwnProperty("select_list_name") &&
        field.select_list_name
      ) {
        value = this.getOptionLabel(field.select_list_name, value);
      } else if (
        field.input_type === "select" &&
        field.var_name === this.prefix + "_material_id"
      ) {
        let material = this.materialOptions.find(
          option => option.sm_id === object[field.var_name]
        );
        if (material) {
          value = this.materialOptionsNormalizer(material).label;
        }
      } else if (
        field.input_type === "radio" &&
        field.hasOwnProperty("var_name") &&
        field.var_name
      ) {
        value = this.getOptionLabel(field.var_name, value);
      }
      return value;
    },

    /**
     * Gets the label corresponding to the value of a given select variable
     * @param {Object} optionList - list being analysed
     * @param id
     * @return {String | Number}
     */
    getOptionLabel(optionList, id) {
      if (this.options[optionList] !== undefined) {
        let option = this.options[optionList].find(item => item.id == id);
        if (option !== undefined) {
          return option.label;
        } else return id;
      } else {
        return id;
      }
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getCheckboxFields(field) {
      if (field.hasOwnProperty("checkbox_options")) {
        for (const option of field.checkbox_options) {
          let checkboxField = option;
          checkboxField.input_type = field.input_type;
          this.fields.push(checkboxField);
        }
      }
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getRadioOptions(field) {
      if (field.hasOwnProperty("var_name") && field.var_name) {
        let varName = field.var_name;
        this.options[varName] = [];
        if (field.hasOwnProperty("radio_options")) {
          for (const option of field.radio_options) {
            let radioOption = {
              id: option.value,
              label: option.label
            };
            this.options[varName].push(radioOption);
          }
        }
      }
    },

    /**
     * Obtains the options for a given select list.
     * @param {String} listName - name of the list to use
     */
    getSelectOptions(listName) {
      if (
        !this.options.hasOwnProperty(listName) ||
        this.options[listName].length === 0
      ) {
        let list = this.$store.getters.getListOfOptionsByListName(
          listName,
          [],
          true
        );

        if (list) {
          this.options[listName] = [];
          for (const option of list) {
            this.options[listName].push(this.normalizer(option));
          }
        }
      }
    },

    loadUserOptions({ callback }) {
      this.fetchAllUsers()
        .then(resp => {
          this.optionsUser = resp.data.data;
        })
        .catch(error => {
          console.error(error);
        })
        .then(() => {
          callback();
        });
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param {Object} node - option being normalized.
     * @returns {Object}
     */
    normalizer(node) {
      let id = "";
      let label = "";
      switch (true) {
        case node.hasOwnProperty("code"):
          id = node.code;
          if (node.hasOwnProperty("client_label") && node.client_label) {
            label = node.client_label;
          } else if (node.hasOwnProperty("libelle") && node.libelle) {
            label = node.libelle;
          } else if (node.hasOwnProperty("reference") && node.reference) {
            //cableMaterialList
            label = node.reference;
          } else if (node.hasOwnProperty("name") && node.name) {
            //cableMaterialList
            label = node.name;
          } else if (
            node.hasOwnProperty("municipality_label") &&
            node.municipality_label
          ) {
            label = node.municipality_label;
          }
          break;
        case node.hasOwnProperty("or_id"): //OwnerList
          id = node.or_id;
          label = node.or_nom;
          break;
        case node.hasOwnProperty("pw_id"): //PathwayIdsList
          id = node.pw_id;
          label = node.pw_id;
          break;
      }
      return {
        id: id,
        label: label,
        isDisabled: node.deleted
      };
    },

    /**
     * Checks if the value of a field is different from the original one.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    valueWasChanged(objectIndex, field) {
      let originalValue = this.originalObjects[objectIndex][field.var_name];
      let newValue = this.objects[objectIndex][field.var_name];
      if (
        originalValue === null &&
        (newValue === "" || newValue === undefined || newValue === null)
      ) {
        return false;
      } else {
        return originalValue != newValue;
      }
    },

    /**
     * Checks if the value of a required field was removed.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    requiredWasMadeEmpty(objectIndex, field) {
      return field.required && !this.objects[objectIndex][field.var_name];
    },

    /**
     * Gets the original value of a given variable
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {Object} field - field being analysed
     * @param value
     * @return {String | Number}
     */
    setValue(objectIndex, field, value) {
      let object = this.objects[objectIndex];
      _.set(object, field.var_name, value);
      this.updateDataToSend(objectIndex, field.var_name, value);
    },

    /**
     * Places the new value in an array to send to the server.
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {String} varName
     * @param {String | Number} newValue
     */
    updateDataToSend(objectIndex, varName, newValue) {
      let uuid = this.getObjectUUID(this.objects[objectIndex]);
      let newVarName = this.getDataToSendVarName(varName);
      let editedObjectIndex = this.dataToSend.findIndex(
        editedObject => editedObject.uuid === uuid
      );
      if (editedObjectIndex > -1) {
        this.setValueForDataToSend(editedObjectIndex, newVarName, newValue);
        _.set(this.dataToSend[editedObjectIndex], newVarName, newValue);
      } else {
        editedObjectIndex = this.dataToSend.length;
        this.dataToSend.push({ uuid: uuid });
        this.setValueForDataToSend(editedObjectIndex, newVarName, newValue);
      }
    },
    setValueForDataToSend(index, varName, newValue) {
      _.set(this.dataToSend[index], varName, newValue);
    },

    getDataToSendVarName(varName) {
      let updatedVarName = varName;
      if (varName.startsWith(this.initials)) {
        updatedVarName = varName.replace(this.initials + "_", "");
      } else if (varName.startsWith("address.ad_")) {
        updatedVarName = varName.replace("address.ad_", "address.");
      }
      return updatedVarName;
    },

    closeEditSelected() {
      this.editSelectedOpen = false;
      bus.$emit("multiple-selection-deactivate-edit-selected");
    },

    /**
     * Applies the new values to all the selected objects
     * @param {Object} newValues
     */
    applyMassEdition(newValues) {
      this.closeEditSelected();
      this.selected.forEach(objectIndex => {
        for (let key in newValues) {
          _.set(this.objects[objectIndex], key, newValues[key]);
          this.updateDataToSend(objectIndex, key, newValues[key]);
        }
      });
    },

    /**
     * Saves the data that was changed.
     */
    save() {
      if (this.dataToSend.length !== 0) {
        this.EDIT_OBJECTS({
          endpoint: this.endpoint,
          data: this.dataToSend
        })

          .then(() => this.updateTable())
          .catch(error => console.error(error));
      }
      else this.updateTable();

    },

    /**
     * Refresh the data shown.
     */
    refresh() {
      if (this.fields.length > 0) {
        this.getObjects();
        bus.$emit("remove-highlight-from-selected-table");
        bus.$emit("update-geoserver");
      }
    },

    getDataToExport() {
      let dataToExport = this.getDataToExportMainContent();
      dataToExport.unshift(this.dataToExportHeaders);
      bus.$emit("multiple-selection-set-data-to-export", dataToExport);
    },

    getDataToExportMainContent() {
      let items = [];
      for (const objectIndex of this.selected) {
        const preparedObject = this.prepareObjectForExportation(objectIndex);
        items.push(preparedObject);
      }
      return items;
    },

    prepareObjectForExportation(objectIndex) {
      const object = this.objects[objectIndex];

      let preparedObject = { id: object[this.prefix + "_id"] };

      for (const field of this.fields) {
        let value = this.getValue(object, field);
        if (field.input_type === "checkbox") {
          value = !!value;
        } else if (field.input_type === "select" && value) {
          if (field.var_name === this.prefix + "_material_id") {
            value = this.materialOptions.find(option => option.id === value);
          } else {
            let options = this.getSpecificListOfOptions(field);
            if (options) {
              let item = options.find(option => option.id === value);
              if (item) {
                value = item.client_label
                  ? item.client_label
                  : item.label
                  ? item.label
                  : item.libelle;
              }
            }
          }
        } else {
          value = value === undefined || value === null ? "" : value;
        }
        preparedObject[field.var_name] = value;
      }

      return preparedObject;
    },

    /**
     * Clear the data to send object.
     */
    clearDataToSend() {
      this.dataToSend = [];
    },

    getInputRef(objectIndex, fieldIndex) {
      return objectIndex * 1000 + fieldIndex;
    },

    changePositionFocus(objectIndex, fieldIndex, valueChange) {
      let ref = this.getInputRef(objectIndex, fieldIndex);
      let nextRef = ref + valueChange;
      let nextFieldIndex = nextRef % 1000;
      let nextObjectIndex = (nextRef - nextFieldIndex) / 1000;

      if (
        nextObjectIndex >= 0 &&
        nextObjectIndex < this.numberObjects &&
        nextFieldIndex >= 0 &&
        nextFieldIndex < this.numberFields
      ) {
        let input = this.$refs[nextRef][0].querySelector("input");
        if (input === null || input.disabled) {
          nextRef = nextRef + valueChange;
          this.changePositionFocus(
            nextObjectIndex,
            nextFieldIndex,
            valueChange
          );
        } else {
          input.focus();
        }
      }
    },

    //Standby because with many objects the application blocks
    sortTable(n) {
      let table,
        rows,
        i,
        x,
        y,
        shouldSwitch,
        switchcount = 0;
      table = document.getElementById("myTable");
      let switching = true;
      //Set the sorting direction to ascending:
      let dir = "asc";
      /*Make a loop that will continue until no switching has been done:*/
      while (switching) {
        //start by saying: no switching is done:
        switching = false;
        rows = table.rows;
        /*Loop through all table rows (except the first, which contains table headers):*/
        for (i = 1; i < rows.length - 1; i++) {
          //start by saying there should be no switching:
          shouldSwitch = false;
          /*Get the two elements you want to compare, one from current row and one from the next:*/
          x = rows[i].getElementsByTagName("TD")[n];
          y = rows[i + 1].getElementsByTagName("TD")[n];
          /*check if the two rows should switch place, based on the direction, asc or desc:*/
          if (dir === "asc") {
            if (x.innerHTML.toLowerCase() > y.innerHTML.toLowerCase()) {
              //if so, mark as a switch and break the loop:
              shouldSwitch = true;
              break;
            }
          } else if (dir === "desc") {
            if (x.innerHTML.toLowerCase() < y.innerHTML.toLowerCase()) {
              //if so, mark as a switch and break the loop:
              shouldSwitch = true;
              break;
            }
          }
        }
        if (shouldSwitch) {
          /*If a switch has been marked, make the switch and mark that a switch has been done:*/
          rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
          switching = true;
          //Each time a switch is done, increase this count by 1:
          switchcount++;
        } else {
          /*If no switching has been done AND the direction is "asc",
      set the direction to "desc" and run the while loop again.*/
          if (switchcount === 0 && dir === "asc") {
            dir = "desc";
            switching = true;
          }
        }
      }
    },

    ///////////////////////////////
    /* Stock Material methods */
    ///////////////////////////////

    /**
     * Get all material options
     * @returns {Promise<void>}
     */
    async loadMaterialOptions() {
      try {
        let queryParameters = {
          material_family_id: this.prefix
        };
        let result = await this.fetchStockMaterialsList(queryParameters);
        let options = _.cloneDeep(result);
        options.sort((a, b) => {
          let nameA = a.sm_short_description
            ? a.sm_short_description
            : a.sm_description;
          let nameB = b.sm_short_description
            ? b.sm_short_description
            : b.sm_description;
          nameA = JSON.stringify(nameA).toLowerCase();
          nameB = JSON.stringify(nameB).toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        this.materialOptions = options;
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Normalizer for the list of materials options (so that the treeselect component can read it).
     * @param {object} node - Original material object
     * @return {object} the adapted object
     */
    materialOptionsNormalizer(node) {
      let label = node.sm_description;
      if (node.sm_short_description) {
        label = node.sm_short_description;
      }
      return {
        id: node.sm_id,
        label: label
      };
    }
  }
};
</script>

<style scoped>
.no-select {
  user-select: none;
}

.with-select {
  user-select: text;
}
th {
  position: sticky;
  top: 0;
  background-color: #f3f5f7;
  z-index: 1;
}
input:invalid,
textarea:invalid {
  border-bottom: 1px solid var(--red);
}

.load-data-button {
  margin-top: 80px;
}

.header {
  height: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/MultiSelectionForm/MultiSelectionTable.vue